import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type {
  ExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/excalidraw/element/types";
import type {
  Collaborator as ExcalidrawCollaborator,
  SocketId,
} from "@excalidraw/excalidraw/types";
import {
  reconcileElements,
  CaptureUpdateAction,
  getSceneVersion,
  UserIdleState,
} from "@excalidraw/excalidraw";
import {
  WSMessageType,
  createBinaryFrame,
  electSaveLeader,
  parseBinaryFrame,
  type SessionResponse,
  type RoomInviteTokens,
  type CursorPayload,
  type UserChangePayload,
  type CollaborationParticipant,
  type SaveAckPayload,
  type SaveFailedPayload,
  type SaveRequestPayload,
  API_PATHS,
} from "@excalidraw-ms/shared";
import { BACKEND_API_BASE } from "../env";
import { toWebSocketBuffer } from "./wsBuffer";
import { restoreScene } from "../utils/sceneHelpers";
import type { ExcalidrawSceneData } from "@excalidraw-ms/shared";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Collaborator {
  pointer?: { x: number; y: number };
  button?: string;
  username?: string;
  id?: string;
  socketId?: string;
  selectedElementIds?: Record<string, boolean>;
  userState?: typeof UserIdleState.ACTIVE | typeof UserIdleState.IDLE | typeof UserIdleState.AWAY;
  avatarUrl?: string;
  color?: { background: string; stroke: string };
}

interface PendingSaveRequest {
  requestedVersion: number;
  requestedFingerprint?: string;
  resolve: (payload: SaveAckPayload) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface PointerUpdatePayload {
  pointer: { x: number; y: number; tool: string };
  button: string;
}

interface CollabClientOptions {
  excalidrawAPI: ExcalidrawImperativeAPI;
  getToken: () => Promise<string>;
  /** File-backed mode: driveId + itemId required */
  driveId?: string;
  itemId?: string;
  drawingId?: string;
  shareToken?: string;
  /** Ephemeral mode: signed invite token when joining an existing room */
  inviteToken?: string;
  username?: string;
  onConnectionChange?: (connected: boolean) => void;
  onRoomUserCountChange?: (count: number) => void;
  onParticipantsChange?: (
    participants: readonly CollaborationParticipant[],
    confirmed: boolean,
  ) => void;
  onETagUpdate?: (eTag: string) => void;
  onRemoteUpdate?: () => void;
  onSaveAckUpdate?: (
    eTag: string,
    sceneVersion: number,
    requestId?: string,
    sceneFingerprint?: string,
  ) => void;
  /** Fired when this client (leader) receives a save request from a follower. */
  onSaveRequest?: (
    requestId: string,
    requestedVersion: number,
    requestedFingerprint?: string,
  ) => void;
  /** Fired when this client becomes the leader after being a follower (previous leader left).
   *  EditorPage should immediately trigger a save when this fires. */
  onLeaderTakeover?: (pendingRequestIds: string[]) => void;
  /** Fired when the session terminates with no further reconnect possible.
   *  NOT fired during cleanup/destroy (unmount) — only for organic disconnects
   *  and user-initiated quit(). */
  onSessionEnd?: () => void;
  /** Fired when an ephemeral room is created/joined — returns the invite token
   *  matching this client's own role, for the URL. */
  onRoomIdAssigned?: (roomId: string, selfInviteToken: string) => void;
  /** Fired for ephemeral sessions once the server has resolved this client's
   *  role and whether it started the room (and so may hand out links). */
  onSessionRoleResolved?: (accessLevel: "editor" | "viewer", canCreateLinks: boolean) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 50;
const MAX_RECONNECT_ATTEMPTS = 5;
const MAX_BACKOFF_MS = 30_000;
const SAVE_REQUEST_TIMEOUT_MS = 30_000;
const IDLE_THRESHOLD_MS = 60_000; // 1 minute → IDLE
const AWAY_THRESHOLD_MS = 180_000; // 3 minutes → AWAY
const IDLE_CHECK_INTERVAL_MS = 15_000; // check every 15s
const AI_AVATAR = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#6965db" d="M15 2h2v5h-2z"/><rect x="5" y="7" width="22" height="21" rx="5" fill="#6965db"/><path stroke="#fff" stroke-width="3" d="M10 16h3m6 0h3M11 23h10"/></svg>',
)}`;

// ---------------------------------------------------------------------------
// CollabClient
// ---------------------------------------------------------------------------

export class CollabClient {
  private ws: WebSocket | null = null;
  private excalidrawAPI: ExcalidrawImperativeAPI;
  private getToken: () => Promise<string>;
  private driveId?: string;
  private itemId?: string;
  private drawingId?: string;
  private ephemeralRoomId?: string;
  private inviteToken?: string;
  private inviteTokens?: RoomInviteTokens;
  private collaborators = new Map<string, Collaborator>();
  private verifiedAiParticipants = new Map<string, CollaborationParticipant>();
  private onParticipantsChange?: CollabClientOptions["onParticipantsChange"];
  private lastRemoteUpdateVersion = -1;
  private lastBroadcastSnapshot = new Map<string, number>();
  private isConnected = false;
  private synchronized = false;
  private synchronizedParticipantIds = new Set<string>();
  private myClientId: string | null = null;
  private peerClientIds: string[] = [];
  private editorPeerClientIds: string[] = [];
  /** Backend-verified AI participants; they save only when no human editor is present. */
  private aiParticipantIds = new Set<string>();
  private accessLevel: "editor" | "viewer" = "editor";
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private broadcastDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingElements: readonly ExcalidrawElement[] | null = null;
  private username?: string;
  private shareToken?: string;
  private onConnectionChange?: (connected: boolean) => void;
  private onRoomUserCountChange?: (count: number) => void;
  private onETagUpdate?: (eTag: string) => void;
  private onRemoteUpdate?: () => void;
  private onSaveAckUpdate?: CollabClientOptions["onSaveAckUpdate"];
  private onSaveRequest?: CollabClientOptions["onSaveRequest"];
  private onLeaderTakeover?: (pendingRequestIds: string[]) => void;
  private onSessionEnd?: () => void;
  private onRoomIdAssigned?: (roomId: string, selfInviteToken: string) => void;
  private onSessionRoleResolved?: (
    accessLevel: "editor" | "viewer",
    canCreateLinks: boolean,
  ) => void;
  private destroyed = false;
  private userInitiatedDisconnect = false;
  private connectionAttempt = 0;
  private lastActivityTimestamp = Date.now();
  private currentIdleState: string = UserIdleState.ACTIVE;
  private idleCheckTimer: ReturnType<typeof setInterval> | null = null;
  private avatarUrl?: string;
  private color?: { background: string; stroke: string };
  private pendingSaveRequests = new Map<string, PendingSaveRequest>();

  constructor(opts: CollabClientOptions) {
    this.excalidrawAPI = opts.excalidrawAPI;
    this.getToken = opts.getToken;
    this.driveId = opts.driveId;
    this.itemId = opts.itemId;
    this.drawingId = opts.drawingId;
    this.inviteToken = opts.inviteToken;
    this.shareToken = opts.shareToken;
    this.username = opts.username;
    this.onConnectionChange = opts.onConnectionChange;
    this.onRoomUserCountChange = opts.onRoomUserCountChange;
    this.onParticipantsChange = opts.onParticipantsChange;
    this.onETagUpdate = opts.onETagUpdate;
    this.onRemoteUpdate = opts.onRemoteUpdate;
    this.onSessionEnd = opts.onSessionEnd;
    this.onRoomIdAssigned = opts.onRoomIdAssigned;
    this.onSessionRoleResolved = opts.onSessionRoleResolved;
    this.onSaveAckUpdate = opts.onSaveAckUpdate;
    this.onSaveRequest = opts.onSaveRequest;
    this.onLeaderTakeover = opts.onLeaderTakeover;
  }

  /** Whether this client is in ephemeral (scene-only collab) mode */
  getIsEphemeral(): boolean {
    return !this.driveId || !this.itemId;
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  async connect(): Promise<void> {
    if (this.destroyed) return;
    const attempt = ++this.connectionAttempt;
    this.userInitiatedDisconnect = false;

    try {
      const token = await this.getToken();
      if (attempt !== this.connectionAttempt) return;
      let wsEndpoint: string;
      let sessionToken: string;

      if (this.driveId && this.itemId) {
        if (!this.drawingId) {
          throw new Error("Canonical drawing ID is required for file collaboration");
        }
        // File-backed mode: POST /api/session { mode: "file" }
        const res = await fetch(`${BACKEND_API_BASE}${API_PATHS.session}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mode: "file",
            driveId: this.driveId,
            itemId: this.itemId,
            ...(this.drawingId ? { drawingId: this.drawingId } : {}),
            ...(this.shareToken ? { shareToken: this.shareToken } : {}),
          }),
        });

        if (!res.ok) {
          throw new Error(`Session join failed: ${res.status}`);
        }

        const session: SessionResponse = await res.json();
        if (attempt !== this.connectionAttempt) return;
        if (session.accessLevel !== "editor" && session.accessLevel !== "viewer") {
          throw new Error("Session response is missing a valid access level");
        }
        this.accessLevel = session.accessLevel;
        this.avatarUrl = session.avatarUrl ?? undefined;
        this.color = session.color;
        wsEndpoint = session.wsEndpoint;
        sessionToken = session.sessionToken;
      } else {
        // Scene-only mode: POST /api/session { mode: "scene" }
        const res = await fetch(`${BACKEND_API_BASE}${API_PATHS.session}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mode: "scene",
            ...(this.inviteToken ? { inviteToken: this.inviteToken } : {}),
          }),
        });

        if (!res.ok) {
          throw new Error(`Scene session failed: ${res.status}`);
        }

        const session: SessionResponse = await res.json();
        if (attempt !== this.connectionAttempt) return;
        if (session.accessLevel !== "editor" && session.accessLevel !== "viewer") {
          throw new Error("Session response is missing a valid access level");
        }
        this.color = session.color;
        this.ephemeralRoomId = session.roomId;
        this.accessLevel = session.accessLevel;
        this.inviteTokens = session.inviteTokens;
        wsEndpoint = session.wsEndpoint;
        sessionToken = session.sessionToken;

        // Hold on to our own token so a reconnect rejoins this room at the same
        // access level rather than creating a fresh one.
        const selfInviteToken = session.selfInviteToken ?? this.inviteToken;
        if (selfInviteToken) this.inviteToken = selfInviteToken;

        this.onSessionRoleResolved?.(this.accessLevel, !!session.inviteTokens);
        // Notify caller of the assigned room (for URL update)
        if (selfInviteToken) this.onRoomIdAssigned?.(session.roomId, selfInviteToken);
      }

      // Open WebSocket — no token in URL
      const ws = new WebSocket(wsEndpoint);
      ws.binaryType = "arraybuffer";
      this.ws = ws;

      ws.onopen = () => {
        // Send auth as Text Frame
        ws.send(JSON.stringify({ type: "auth", token: sessionToken }));
      };

      ws.onmessage = (event: MessageEvent) => {
        this.handleMessage(event);
      };

      ws.onclose = (event: CloseEvent) => {
        this.handleClose(event);
      };

      ws.onerror = () => {
        // Error triggers close — handled there
      };
    } catch (err) {
      if (attempt !== this.connectionAttempt) return;
      console.error("[CollabClient] connect failed:", err);
      // Non-retryable errors (authorization/session gone, bad request) — don't reconnect
      const msg = String(err);
      if (msg.includes("403") || msg.includes("404") || msg.includes("400")) {
        this.onSessionEnd?.();
        return;
      }
      this.scheduleReconnect(false);
    }
  }

  disconnect(): void {
    ++this.connectionAttempt;
    this.userInitiatedDisconnect = true;
    this.cancelTimers();
    this.rejectPendingSaveRequests("Collaboration session ended");
    this.stopIdleDetection();
    if (this.ws) {
      try {
        this.ws.close(1000, "User disconnect");
      } catch {
        // ignore
      }
      this.ws = null;
    }
    this.setConnected(false);
    this.collaborators.clear();
    this.verifiedAiParticipants.clear();
    this.onParticipantsChange?.([], false);
    this.peerClientIds = [];
    this.editorPeerClientIds = [];
    this.aiParticipantIds.clear();
    this.myClientId = null;
    this.reconnectAttempts = 0;
  }

  async toggle(): Promise<void> {
    if (this.isConnected) {
      this.disconnect();
    } else {
      await this.connect();
    }
  }

  /** Whether this client is the elected cloud-save leader (humans before verified AI peers). */
  getIsLeader(): boolean {
    if (!this.synchronized || this.accessLevel === "viewer" || !this.myClientId) return false;
    return this.getSaveLeaderClientId() === this.myClientId;
  }

  hasOtherSaveCapableEditor(): boolean {
    if (!this.myClientId) return false;
    return this.editorPeerClientIds.some(
      (id) => id !== this.myClientId && this.synchronizedParticipantIds.has(id),
    );
  }

  flushPendingUpdates(): void {
    if (this.broadcastDebounceTimer) {
      clearTimeout(this.broadcastDebounceTimer);
      this.broadcastDebounceTimer = null;
    }
    this.flushBroadcast();
  }
  /** Refresh a persistence baseline without discarding newer live edits or local gestures. */
  reconcileCloudScene(scene: ExcalidrawSceneData): void {
    if (!this.isConnected || !this.getIsLeader()) {
      throw new Error("Collaboration save leadership changed");
    }
    const restored = restoreScene(scene);
    const remoteElements: readonly OrderedExcalidrawElement[] = restored.elements;
    const reconciled = reconcileElements(
      this.excalidrawAPI.getSceneElementsIncludingDeleted(),
      remoteElements as Parameters<typeof reconcileElements>[1],
      this.excalidrawAPI.getAppState(),
    );
    const existingFiles = this.excalidrawAPI.getFiles();
    this.excalidrawAPI.addFiles(
      Object.values(restored.files).filter((file) => !Object.hasOwn(existingFiles, file.id)),
    );
    this.lastRemoteUpdateVersion = getSceneVersion(reconciled as ExcalidrawElement[]);
    this.excalidrawAPI.updateScene({
      elements: reconciled,
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    this.sendFrame(
      createBinaryFrame(
        WSMessageType.SCENE_UPDATE,
        new TextEncoder().encode(JSON.stringify(reconciled)),
      ),
    );
  }
  /** This client's access level in the current session. */
  getAccessLevel(): "editor" | "viewer" {
    return this.accessLevel;
  }

  getParticipantId(): string | null {
    return this.isConnected ? this.myClientId : null;
  }

  /**
   * Invite token for a given role in this ephemeral room, or undefined when the
   * server did not issue one (viewers never receive an editor token).
   */
  getInviteToken(role: "editor" | "viewer"): string | undefined {
    return role === "editor" ? this.inviteTokens?.editor : this.inviteTokens?.viewer;
  }

  /** Broadcast a SAVE_ACK to peers after successful save */
  broadcastSaveAck(
    eTag: string,
    sceneVersion: number,
    requestId?: string,
    sceneFingerprint?: string,
  ): void {
    if (!this.isConnected || !this.ws || !this.myClientId) return;
    const payload: SaveAckPayload = {
      eTag,
      savedBy: this.myClientId,
      sceneVersion,
      ...(requestId ? { requestId } : {}),
      ...(sceneFingerprint ? { sceneFingerprint } : {}),
    };
    if (requestId) {
      this.resolvePendingSaveRequest(payload);
    }
    const encoded = new TextEncoder().encode(JSON.stringify(payload));
    const frame = createBinaryFrame(WSMessageType.SAVE_ACK, encoded);
    try {
      this.sendFrame(frame);
    } catch {
      // ignore
    }
  }

  broadcastSaveFailed(requestId: string): void {
    if (!this.isConnected || !this.ws || !this.myClientId) return;
    const payload: SaveFailedPayload = { requestId, savedBy: this.myClientId };
    this.rejectPendingSaveRequest(requestId, "Collaborative save failed");
    const encoded = new TextEncoder().encode(JSON.stringify(payload));
    const frame = createBinaryFrame(WSMessageType.SAVE_FAILED, encoded);
    try {
      this.sendFrame(frame);
    } catch {
      // ignore
    }
  }

  /** Request current leader to perform an immediate save (follower Ctrl+S path). */
  requestSaveFromLeader(
    requestedVersion: number,
    requestedFingerprint?: string,
  ): Promise<SaveAckPayload> {
    if (!this.isConnected || !this.ws || !this.myClientId) {
      return Promise.reject(new Error("Collaboration is not connected"));
    }
    const requestId = crypto.randomUUID();
    return new Promise<SaveAckPayload>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingSaveRequests.delete(requestId);
        reject(new Error("Collaborative save timed out"));
      }, SAVE_REQUEST_TIMEOUT_MS);
      this.pendingSaveRequests.set(requestId, {
        requestedVersion,
        requestedFingerprint,
        resolve,
        reject,
        timer,
      });
      try {
        this.sendSaveRequest(requestId, requestedVersion, requestedFingerprint);
      } catch (error) {
        clearTimeout(timer);
        this.pendingSaveRequests.delete(requestId);
        reject(error);
      }
    });
  }

  /**
   * Called from onChange — broadcasts local edits to collaborators.
   * Uses version-based guard to skip remote updates and debounce.
   * Returns whether the scene change originated locally.
   */
  broadcastUpdate(elements: readonly ExcalidrawElement[]): boolean {
    // Version-based guard: skip if this onChange was triggered by a remote update
    const currentVersion = getSceneVersion(elements as ExcalidrawElement[]);
    if (currentVersion === this.lastRemoteUpdateVersion) return false;

    if (!this.isConnected || !this.ws) return true;

    // Any local scene edit resets idle state
    this.reportActivity();

    this.pendingElements = elements;
    if (this.broadcastDebounceTimer) return true; // debounce in progress

    this.broadcastDebounceTimer = setTimeout(() => {
      this.broadcastDebounceTimer = null;
      this.flushBroadcast();
    }, DEBOUNCE_MS);
    return true;
  }

  /** Called from onPointerUpdate — broadcasts cursor position, selections, and idle state */
  broadcastCursor(payload: PointerUpdatePayload): void {
    if (!this.isConnected || !this.ws || !this.myClientId) return;

    // Any pointer activity resets idle state
    this.reportActivity();

    const appState = this.excalidrawAPI.getAppState();

    const cursorPayload: CursorPayload = {
      pointer: payload.pointer,
      button: payload.button,
      userId: this.myClientId,
      username: this.username,
      selectedElementIds: appState.selectedElementIds as Record<string, boolean>,
      userState: this.currentIdleState,
      avatarUrl: this.avatarUrl,
      color: this.color,
    };

    const encoded = new TextEncoder().encode(JSON.stringify(cursorPayload));
    const frame = createBinaryFrame(WSMessageType.CURSOR_UPDATE, encoded);
    try {
      this.sendFrame(frame);
    } catch {
      // ignore
    }
  }

  getIsConnected(): boolean {
    return this.isConnected;
  }

  /** User-initiated quit: fires onSessionEnd then cleans up. */
  quit(): void {
    this.onSessionEnd?.();
    this.destroy();
  }

  destroy(): void {
    this.destroyed = true;
    this.disconnect();
  }

  // -----------------------------------------------------------------------
  // Idle detection
  // -----------------------------------------------------------------------

  /** Reset idle timer on any local activity */
  private reportActivity(): void {
    this.lastActivityTimestamp = Date.now();
    if (this.currentIdleState !== UserIdleState.ACTIVE) {
      this.currentIdleState = UserIdleState.ACTIVE;
    }
  }

  private startIdleDetection(): void {
    this.stopIdleDetection();
    this.lastActivityTimestamp = Date.now();
    this.currentIdleState = UserIdleState.ACTIVE;
    this.idleCheckTimer = setInterval(() => this.checkIdleState(), IDLE_CHECK_INTERVAL_MS);
  }

  private stopIdleDetection(): void {
    if (this.idleCheckTimer) {
      clearInterval(this.idleCheckTimer);
      this.idleCheckTimer = null;
    }
    this.currentIdleState = UserIdleState.ACTIVE;
  }

  private checkIdleState(): void {
    const elapsed = Date.now() - this.lastActivityTimestamp;
    let newState: string;
    if (elapsed >= AWAY_THRESHOLD_MS) {
      newState = UserIdleState.AWAY;
    } else if (elapsed >= IDLE_THRESHOLD_MS) {
      newState = UserIdleState.IDLE;
    } else {
      newState = UserIdleState.ACTIVE;
    }

    if (newState !== this.currentIdleState) {
      this.currentIdleState = newState;
      // Broadcast the state change immediately so peers see it without waiting for pointer movement
      this.broadcastIdleState();
    }
  }

  /** Send a cursor update with current idle state (no pointer change) */
  private broadcastIdleState(): void {
    if (!this.isConnected || !this.ws || !this.myClientId) return;

    const appState = this.excalidrawAPI.getAppState();
    const cursorPayload: CursorPayload = {
      pointer: { x: 0, y: 0, tool: "idle" },
      button: "up",
      userId: this.myClientId,
      username: this.username,
      selectedElementIds: appState.selectedElementIds as Record<string, boolean>,
      userState: this.currentIdleState,
      avatarUrl: this.avatarUrl,
      color: this.color,
    };
    const encoded = new TextEncoder().encode(JSON.stringify(cursorPayload));
    const frame = createBinaryFrame(WSMessageType.CURSOR_UPDATE, encoded);
    try {
      this.sendFrame(frame);
    } catch {
      // ignore
    }
  }

  // -----------------------------------------------------------------------
  // Private
  // -----------------------------------------------------------------------

  private handleMessage(event: MessageEvent): void {
    const data = event.data;

    // Text Frame — auth-ok (pre-auth state)
    if (typeof data === "string") {
      try {
        const msg = JSON.parse(data);
        if (msg.type === "auth-ok") {
          this.resetSaveElectionState();
          this.myClientId = msg.clientId ?? null;
          this.setConnected(true);
          this.reconnectAttempts = 0;
          this.startIdleDetection();
        }
      } catch {
        // ignore malformed text
      }
      return;
    }

    // Binary Frame — post-auth messages
    if (data instanceof ArrayBuffer) {
      try {
        const frame = parseBinaryFrame(new Uint8Array(data));
        this.handleBinaryMessage(frame.type, frame.payload);
      } catch {
        // ignore malformed frames
      }
    }
  }

  private handleBinaryMessage(type: WSMessageType, payload: Uint8Array): void {
    switch (type) {
      case WSMessageType.SCENE_UPDATE:
        this.handleRemoteSceneUpdate(payload);
        break;

      case WSMessageType.CURSOR_UPDATE:
        this.handleRemoteCursor(payload);
        break;

      case WSMessageType.USER_CHANGE:
        this.handleUserChange(payload);
        break;

      case WSMessageType.FIRST_IN_ROOM:
        this.markSynchronized();
        break;

      case WSMessageType.SCENE_REQUEST:
        this.handleSceneRequest();
        break;

      case WSMessageType.SCENE_PUSH:
        this.handleScenePush(payload);
        break;

      case WSMessageType.SAVE_ACK:
        this.handleSaveAck(payload);
        break;

      case WSMessageType.SAVE_REQUEST:
        this.handleSaveRequest(payload);
        break;

      case WSMessageType.SAVE_FAILED:
        this.handleSaveFailed(payload);
        break;

      default:
        break;
    }
  }

  private handleRemoteSceneUpdate(payload: Uint8Array): void {
    try {
      const remoteElements = JSON.parse(new TextDecoder().decode(payload));
      const localElements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
      const appState = this.excalidrawAPI.getAppState();

      const reconciled = reconcileElements(
        localElements as unknown as readonly OrderedExcalidrawElement[],
        remoteElements,
        appState,
      );

      // Store version BEFORE updateScene so the guard works
      const reconciledVersion = getSceneVersion(reconciled as ExcalidrawElement[]);
      this.lastRemoteUpdateVersion = reconciledVersion;

      this.excalidrawAPI.updateScene({
        elements: reconciled,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
      this.onRemoteUpdate?.();
    } catch (err) {
      console.error("[CollabClient] Failed to apply remote scene update:", err);
    }
  }

  private handleRemoteCursor(payload: Uint8Array): void {
    try {
      const cursor: CursorPayload = JSON.parse(new TextDecoder().decode(payload));
      // Skip our own cursor (relayed back by excalidraw-room)
      if (cursor.userId === this.myClientId) return;
      if (!this.peerClientIds.includes(cursor.userId)) return;
      const verified = this.verifiedAiParticipants.get(cursor.userId);
      this.collaborators.set(cursor.userId, {
        pointer: cursor.pointer,
        button: cursor.button,
        username: verified?.identity?.displayName ?? cursor.username,
        id: cursor.userId,
        selectedElementIds: cursor.selectedElementIds,
        userState: verified
          ? verified.activity === "idle"
            ? UserIdleState.IDLE
            : UserIdleState.ACTIVE
          : (cursor.userState as Collaborator["userState"]),
        avatarUrl: verified ? AI_AVATAR : cursor.avatarUrl,
        color: cursor.color,
      });
      this.excalidrawAPI.updateScene({
        collaborators: this.collaborators as unknown as Map<SocketId, ExcalidrawCollaborator>,
      });
    } catch {
      // ignore
    }
  }

  private handleUserChange(payload: Uint8Array): void {
    try {
      const decoded: UserChangePayload | string[] = JSON.parse(new TextDecoder().decode(payload));
      const clientIds = Array.isArray(decoded) ? decoded : decoded.clientIds;
      const editorClientIds = Array.isArray(decoded) ? decoded : decoded.editorClientIds;
      if (
        !Array.isArray(clientIds) ||
        !clientIds.every((id) => typeof id === "string") ||
        !Array.isArray(editorClientIds) ||
        !editorClientIds.every((id) => typeof id === "string")
      ) {
        return;
      }
      const previousLeaderId = this.getSaveLeaderClientId();
      // Detect leader takeover before updating peer list
      const wasLeader = this.getIsLeader();
      const activeSet = new Set(clientIds);
      const participants =
        !Array.isArray(decoded) && Array.isArray(decoded.participants)
          ? decoded.participants.filter(
              (participant) =>
                participant &&
                activeSet.has(participant.participantId) &&
                typeof participant.userId === "string" &&
                typeof participant.canSave === "boolean" &&
                participant.canSave === editorClientIds.includes(participant.participantId) &&
                (!participant.identity ||
                  (participant.identity.kind === "ai" &&
                    participant.identity.sponsorUserId === participant.userId &&
                    typeof participant.identity.displayName === "string" &&
                    participant.identity.displayName.length <= 256)),
            )
          : [];
      // Presence includes viewers; save election includes owners/editors only.
      this.peerClientIds = clientIds;
      this.editorPeerClientIds = editorClientIds;
      this.synchronizedParticipantIds = new Set(
        participants
          .filter((participant) => participant.synchronized === true)
          .map((participant) => participant.participantId),
      );
      this.aiParticipantIds = new Set(
        participants
          .filter((participant) => participant.identity)
          .map((participant) => participant.participantId),
      );
      this.onRoomUserCountChange?.(clientIds.length);
      // Fire takeover if we just became leader (previous leader left)
      const isLeader = this.getIsLeader();
      const nextLeaderId = this.getSaveLeaderClientId();
      if (!wasLeader && isLeader) {
        this.onLeaderTakeover?.(Array.from(this.pendingSaveRequests.keys()));
      } else if (
        nextLeaderId &&
        nextLeaderId !== this.myClientId &&
        nextLeaderId !== previousLeaderId
      ) {
        this.resendPendingSaveRequests();
      }
      // Remove collaborators not in the new list
      for (const id of this.verifiedAiParticipants.keys()) this.collaborators.delete(id);
      this.verifiedAiParticipants.clear();
      for (const participant of participants) {
        if (!participant.identity) continue;
        this.verifiedAiParticipants.set(participant.participantId, participant);
        this.collaborators.set(participant.participantId, {
          ...this.collaborators.get(participant.participantId),
          id: participant.participantId,
          username: participant.identity.displayName,
          avatarUrl: AI_AVATAR,
          userState: participant.activity === "idle" ? UserIdleState.IDLE : UserIdleState.ACTIVE,
        });
      }
      this.onParticipantsChange?.(
        participants,
        !Array.isArray(decoded) &&
          Array.isArray(decoded.participants) &&
          participants.length === decoded.participants.length,
      );
      for (const key of this.collaborators.keys()) {
        if (!activeSet.has(key)) {
          this.collaborators.delete(key);
        }
      }
      this.excalidrawAPI.updateScene({
        collaborators: this.collaborators as unknown as Map<SocketId, ExcalidrawCollaborator>,
      });
    } catch {
      // ignore
    }
  }

  private handleSaveAck(payload: Uint8Array): void {
    try {
      const ack = JSON.parse(new TextDecoder().decode(payload)) as SaveAckPayload;
      if (ack.eTag && typeof ack.sceneVersion === "number") {
        this.onETagUpdate?.(ack.eTag);
        this.onSaveAckUpdate?.(ack.eTag, ack.sceneVersion, ack.requestId, ack.sceneFingerprint);
        if (ack.requestId) {
          this.resolvePendingSaveRequest(ack);
        }
      }
    } catch {
      // ignore
    }
  }

  private handleSaveRequest(payload: Uint8Array): void {
    try {
      const request = JSON.parse(new TextDecoder().decode(payload)) as SaveRequestPayload;
      if (
        !request.requestedBy ||
        !request.requestId ||
        typeof request.requestedVersion !== "number" ||
        request.requestedBy === this.myClientId
      ) {
        return;
      }
      if (this.getIsLeader()) {
        this.onSaveRequest?.(
          request.requestId,
          request.requestedVersion,
          request.requestedFingerprint,
        );
      }
    } catch {
      // ignore
    }
  }

  private handleSaveFailed(payload: Uint8Array): void {
    try {
      const failed = JSON.parse(new TextDecoder().decode(payload)) as SaveFailedPayload;
      if (failed.requestId) {
        this.rejectPendingSaveRequest(failed.requestId, "Collaborative save failed");
      }
    } catch {
      // ignore
    }
  }

  private handleSceneRequest(): void {
    // Serialize full scene and send as SCENE_PUSH
    try {
      const elements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
      const encoded = new TextEncoder().encode(JSON.stringify(elements));
      const frame = createBinaryFrame(WSMessageType.SCENE_PUSH, encoded);
      this.sendFrame(frame);
    } catch (err) {
      console.error("[CollabClient] Failed to push scene:", err);
    }
  }

  private handleScenePush(payload: Uint8Array): void {
    try {
      const remoteElements = JSON.parse(new TextDecoder().decode(payload));
      const localElements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
      const appState = this.excalidrawAPI.getAppState();

      const reconciled = reconcileElements(
        localElements as unknown as readonly OrderedExcalidrawElement[],
        remoteElements,
        appState,
      );
      const version = getSceneVersion(reconciled as ExcalidrawElement[]);
      this.lastRemoteUpdateVersion = version;

      this.excalidrawAPI.updateScene({
        elements: reconciled,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
      this.onRemoteUpdate?.();
      this.markSynchronized();
    } catch (err) {
      console.error("[CollabClient] Failed to apply scene push:", err);
    }
  }

  private markSynchronized(): void {
    const wasLeader = this.getIsLeader();
    this.synchronized = true;
    if (!wasLeader && this.getIsLeader()) {
      this.onLeaderTakeover?.(Array.from(this.pendingSaveRequests.keys()));
    }
  }

  private resetSaveElectionState(): void {
    this.synchronized = false;
    this.editorPeerClientIds = [];
    this.synchronizedParticipantIds.clear();
    this.aiParticipantIds.clear();
  }

  private flushBroadcast(): void {
    if (!this.pendingElements || !this.ws || !this.isConnected) return;

    // Find changed elements by comparing per-element version
    const changed: ExcalidrawElement[] = [];
    for (const el of this.pendingElements) {
      const lastVersion = this.lastBroadcastSnapshot.get(el.id);
      if (lastVersion === undefined || el.version !== lastVersion) {
        changed.push(el as ExcalidrawElement);
        this.lastBroadcastSnapshot.set(el.id, el.version);
      }
    }

    if (changed.length === 0) {
      this.pendingElements = null;
      return;
    }

    const encoded = new TextEncoder().encode(JSON.stringify(changed));
    const frame = createBinaryFrame(WSMessageType.SCENE_UPDATE, encoded);
    try {
      this.sendFrame(frame);
    } catch {
      // ignore
    }
    this.pendingElements = null;
  }

  private getSaveLeaderClientId(): string | null {
    const candidates = this.editorPeerClientIds
      .filter((id) => this.synchronizedParticipantIds.has(id))
      .map((participantId) => ({
        participantId,
        ai: this.aiParticipantIds.has(participantId),
      }));
    return electSaveLeader(candidates) ?? null;
  }

  private sendSaveRequest(
    requestId: string,
    requestedVersion: number,
    requestedFingerprint?: string,
  ): void {
    if (!this.isConnected || !this.ws || !this.myClientId) return;
    const payload: SaveRequestPayload = {
      requestId,
      requestedBy: this.myClientId,
      requestedVersion,
      ...(requestedFingerprint ? { requestedFingerprint } : {}),
    };
    const encoded = new TextEncoder().encode(JSON.stringify(payload));
    this.sendFrame(createBinaryFrame(WSMessageType.SAVE_REQUEST, encoded));
  }

  private resendPendingSaveRequests(): void {
    for (const [requestId, pending] of this.pendingSaveRequests) {
      this.sendSaveRequest(requestId, pending.requestedVersion, pending.requestedFingerprint);
    }
  }

  private resolvePendingSaveRequest(payload: SaveAckPayload): void {
    if (!payload.requestId) return;
    const pending = this.pendingSaveRequests.get(payload.requestId);
    if (!pending) return;
    if (
      payload.sceneVersion < pending.requestedVersion ||
      (pending.requestedFingerprint && payload.sceneFingerprint !== pending.requestedFingerprint)
    ) {
      this.rejectPendingSaveRequest(payload.requestId, "Saved scene does not match the request");
      return;
    }
    clearTimeout(pending.timer);
    this.pendingSaveRequests.delete(payload.requestId);
    pending.resolve(payload);
  }

  private rejectPendingSaveRequest(requestId: string, message: string): void {
    const pending = this.pendingSaveRequests.get(requestId);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pendingSaveRequests.delete(requestId);
    pending.reject(new Error(message));
  }

  private rejectPendingSaveRequests(message: string): void {
    for (const requestId of Array.from(this.pendingSaveRequests.keys())) {
      this.rejectPendingSaveRequest(requestId, message);
    }
  }

  private sendFrame(frame: Uint8Array): void {
    this.ws?.send(toWebSocketBuffer(frame));
  }

  private handleClose(event: CloseEvent): void {
    this.ws = null;
    this.setConnected(false);

    if (this.destroyed) return;
    const userInitiatedDisconnect = this.userInitiatedDisconnect;
    this.userInitiatedDisconnect = false;
    if (userInitiatedDisconnect) return;

    switch (event.code) {
      case 4001: // Auth failure — need fresh session token
        this.scheduleReconnect(true);
        break;
      case 4002: // Collab server unavailable or upstream disconnected
        this.scheduleReconnect(false);
        break;
      case 4003: // Protocol violation — no reconnect
        this.onSessionEnd?.();
        break;
      case 4008: // Slow client — reconnect immediately
        this.scheduleReconnect(false);
        break;
      case 4011: // Superseded by new connection from same user
      case 4012: // Drawing deleted
        this.onSessionEnd?.();
        break;
      case 1000: // Normal transport close from server/proxy
        this.scheduleReconnect(false);
        break;
      default:
        this.scheduleReconnect(false);
        break;
    }
  }

  private scheduleReconnect(_freshToken: boolean): void {
    if (this.destroyed) return;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error("[CollabClient] Max reconnect attempts reached");
      this.onSessionEnd?.();
      return;
    }

    this.reconnectAttempts++;
    const base = Math.min(Math.pow(2, this.reconnectAttempts) * 1000, MAX_BACKOFF_MS);
    const jitter = base * (0.5 + Math.random()); // R2-#6: random jitter

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, jitter);
  }

  private setConnected(connected: boolean): void {
    this.isConnected = connected;
    if (!connected) {
      for (const id of this.verifiedAiParticipants.keys()) this.collaborators.delete(id);
      if (this.verifiedAiParticipants.size) {
        this.excalidrawAPI.updateScene({
          collaborators: this.collaborators as unknown as Map<SocketId, ExcalidrawCollaborator>,
        });
      }
      this.verifiedAiParticipants.clear();
      this.onParticipantsChange?.([], false);
    }
    this.onConnectionChange?.(connected);
  }

  private cancelTimers(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.broadcastDebounceTimer) {
      clearTimeout(this.broadcastDebounceTimer);
      this.broadcastDebounceTimer = null;
    }
  }
}
